const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const TOKEN_KEY = 'pc_token';
export const USER_KEY = 'pc_user';
export const BRIEFING_KEY = 'pc_briefing';

// In-memory fallback for environments where sessionStorage is blocked
// (e.g. strict corporate browser policies)
let _memToken: string | null = null;
let _memUser: string | null = null;

function storageSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* blocked */ }
  if (key === TOKEN_KEY) _memToken = value;
  if (key === USER_KEY)  _memUser  = value;
}

function storageGet(key: string): string | null {
  try {
    const v = sessionStorage.getItem(key);
    if (v !== null) return v;
  } catch { /* blocked */ }
  if (key === TOKEN_KEY) return _memToken;
  if (key === USER_KEY)  return _memUser;
  return null;
}

function storageRemove(key: string): void {
  try { sessionStorage.removeItem(key); } catch { /* blocked */ }
  if (key === TOKEN_KEY) _memToken = null;
  if (key === USER_KEY)  _memUser  = null;
}

export function getToken(): string | null {
  return storageGet(TOKEN_KEY);
}

export function getUser(): { name: string; email: string; role: string } | null {
  try {
    const raw = storageGet(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAuth() {
  storageRemove(TOKEN_KEY);
  storageRemove(USER_KEY);
  storageRemove(BRIEFING_KEY);
  try { sessionStorage.removeItem(BRIEFING_KEY); } catch { /* blocked */ }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    cache: 'no-store',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401) {
    clearAuth();
    window.location.reload();
    throw new ApiError(401, 'Unauthenticated');
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {
      /* ignore parse error */
    }
    throw new ApiError(res.status, detail);
  }

  // 204 No Content — no body to parse
  if (res.status === 204) return undefined as unknown as T;

  return res.json() as Promise<T>;
}

export async function apiLogin(email: string, password: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Invalid credentials');
  }
  const data = await res.json();
  storageSet(TOKEN_KEY, data.access_token);
  const u = data.user || data;
  storageSet(USER_KEY, JSON.stringify({
    name: u.name,
    email: u.email || email,
    role: u.role,
  }));
}
